import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import type {
  AnalysisFilters,
  AttentionItem,
  CoverageAccount,
  CoverageFilters,
  CoverageRow,
  GradeCapacity,
  NaView,
  OwnerCapacity,
  OwnersMode,
  OwnersPortfolioFilter,
  OwnersSortState,
} from "../../Pages/CoverageAnalysis/coverageModel";
import {
  DEFAULT_OWNERS_SORT,
  EMPTY_ANALYSIS,
  EMPTY_FILTERS,
  HEAT_WORST_DIR,
} from "../../Pages/CoverageAnalysis/coverageModel";
import { get } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

const extractContent = (response: any) =>
  response?.data?.content ?? response?.content ?? response;

export type CoverageReturnContext = {
  label: string;
};

export type CoverageView =
  | "overview"
  | "accounts"
  | "owners"
  | "gaps"
  | "attention";

export const COVERAGE_VIEW_LABELS: Record<CoverageView, string> = {
  overview: "Overview",
  accounts: "Accounts",
  owners: "Owners",
  gaps: "Gaps",
  attention: "Needs Attention",
};

export type NaGapsState = {
  type: string; // 'all' | GapType | 'resolved'
  age: string;
  groupBy: "type" | "account";
  sort: "priority" | "oldest";
  lifecycle: "all" | "orphaned" | "covered";
};

export type NaRisksState = {
  type: string; // 'all' | RiskType | 'mitigated'
  groupBy: "type" | "owner" | "account";
};

export const DEFAULT_NA_GAPS: NaGapsState = {
  type: "all",
  age: "all",
  groupBy: "type",
  sort: "priority",
  lifecycle: "all",
};

export const DEFAULT_NA_RISKS: NaRisksState = {
  type: "all",
  groupBy: "type",
};

type CoverageState = {
  accounts: CoverageAccount[];
  rows: CoverageRow[];
  attentionItems: AttentionItem[];
  ownerCapacities: OwnerCapacity[];
  capacityByGrade: GradeCapacity[];
  capacityConfigured: boolean;
  loading: boolean;
  error: string | null;
  view: CoverageView;
  /** Accounts tab: heatmap table vs relationship-strength list. */
  accountsMode: "heatmap" | "strength";
  /** Heatmap column sort from a tile or drill. Null = account name. */
  accountsSort: { key: string; dir: "asc" | "desc" } | null;
  /** Owners summary grouped by over / near / within after the capacity tile. */
  ownersFocus: "capacity" | null;
  /** Owners tab: summary table vs full owner × account grid. */
  ownersMode: OwnersMode;
  /** Owners summary column sort (default worst-first). */
  ownersSort: OwnersSortState;
  /** Full-grid column sort key (account id or "total"); null = summary order. */
  ownersGridSort: string | null;
  /** Highlight owner row after “View in grid →”. */
  ownersHighlight: string | null;
  /** Account modal carousel slide. */
  accountModalSlide: "coverage" | "strength";
  /** @deprecated Tab clicks no longer carry filters. Left null. */
  keptFrom: { from: CoverageView; to: CoverageView } | null;
  /** Per root tab, for the session. A tab click restores this and does not carry the other tab's filters. */
  tabState: Partial<Record<CoverageView, CoverageTabSnapshot>>;
  filters: CoverageFilters;
  analysis: AnalysisFilters;
  groupBy: string;
  compositionLens: string;
  compositionReturn: {
    lensId: string;
    lensLabel: string;
    categoryKey: string;
    categoryLabel: string;
  } | null;
  accountDetailId: string | null;
  linkedCompositionKey: string | null;
  returnContext: CoverageReturnContext | null;
  /** Accounts records side panel. */
  recordsPanel: {
    accountId: string;
    source: string;
    key?: string | null;
  } | null;
  /** Health map focus (dim non-matching accounts). */
  mapFocus: {
    accountId?: string | null;
    contactId?: string | null;
    band?: string | null;
  } | null;
  /** Readiness / not-set-up segment on Accounts. */
  readinessSegment: string;
  /** Owners tab — read-only owner portfolio modal. */
  ownerPortfolio: {
    ownerId: string;
    focusAccountId?: string | null;
    filter?: OwnersPortfolioFilter | null;
  } | null;
  /** Capacity settings (by GL) popup on Owners. */
  capacitySettingsOpen: boolean;
  /** Needs Attention: Gaps | Risks toggle. */
  naView: NaView;
  naGaps: NaGapsState;
  naRisks: NaRisksState;
  /** Optional readiness/composition focus label shown as a chip. */
  naFocusLabel: string | null;
  /**
   * Click-through focus. The chip stays after the side list closes so the
   * account scope still says why it was applied.
   */
  coverageFocus: {
    view: CoverageView;
    fromView: CoverageView;
    label: string;
  } | null;
};

/** Saved with the tab you leave. Restored when you click that tab again. */
type CoverageTabSnapshot = {
  filters: CoverageFilters;
  readinessSegment: string;
  compositionReturn: CoverageState["compositionReturn"];
  accountsMode: CoverageState["accountsMode"];
  accountsSort: CoverageState["accountsSort"];
  ownersFocus: CoverageState["ownersFocus"];
  ownersMode: OwnersMode;
  ownersSort: OwnersSortState;
  ownersGridSort: string | null;
  ownersHighlight: string | null;
  naView: NaView;
  naGaps: NaGapsState;
  naRisks: NaRisksState;
  naFocusLabel: string | null;
  recordsPanel: CoverageState["recordsPanel"];
  mapFocus: CoverageState["mapFocus"];
  linkedCompositionKey: string | null;
  coverageFocus: CoverageState["coverageFocus"];
};

function canonicalCoverageView(view: CoverageView): CoverageView {
  return view === "gaps" ? "attention" : view;
}

function snapshotOf(state: CoverageState): CoverageTabSnapshot {
  return {
    filters: { ...state.filters },
    readinessSegment: state.readinessSegment,
    compositionReturn: state.compositionReturn
      ? { ...state.compositionReturn }
      : null,
    accountsMode: state.accountsMode,
    accountsSort: state.accountsSort ? { ...state.accountsSort } : null,
    ownersFocus: state.ownersFocus,
    ownersMode: state.ownersMode,
    ownersSort: { ...state.ownersSort },
    ownersGridSort: state.ownersGridSort,
    ownersHighlight: state.ownersHighlight,
    naView: state.naView,
    naGaps: { ...state.naGaps },
    naRisks: { ...state.naRisks },
    naFocusLabel: state.naFocusLabel,
    recordsPanel: state.recordsPanel ? { ...state.recordsPanel } : null,
    mapFocus: state.mapFocus ? { ...state.mapFocus } : null,
    linkedCompositionKey: state.linkedCompositionKey,
    coverageFocus: state.coverageFocus ? { ...state.coverageFocus } : null,
  };
}

function applyTabSnapshot(
  state: CoverageState,
  snap: CoverageTabSnapshot | undefined
) {
  if (!snap) {
    state.filters = { ...EMPTY_FILTERS };
    state.readinessSegment = "all";
    state.compositionReturn = null;
    state.accountsMode = "heatmap";
    state.accountsSort = null;
    state.ownersFocus = null;
    state.ownersMode = "summary";
    state.ownersSort = { ...DEFAULT_OWNERS_SORT };
    state.ownersGridSort = null;
    state.ownersHighlight = null;
    state.naView = "gaps";
    state.naGaps = { ...DEFAULT_NA_GAPS };
    state.naRisks = { ...DEFAULT_NA_RISKS };
    state.naFocusLabel = null;
    state.recordsPanel = null;
    state.mapFocus = null;
    state.linkedCompositionKey = null;
    state.coverageFocus = null;
    return;
  }
  state.filters = { ...snap.filters };
  state.readinessSegment = snap.readinessSegment;
  state.compositionReturn = snap.compositionReturn
    ? { ...snap.compositionReturn }
    : null;
  state.accountsMode = snap.accountsMode;
  state.accountsSort = snap.accountsSort ? { ...snap.accountsSort } : null;
  state.ownersFocus = snap.ownersFocus;
  state.ownersMode = snap.ownersMode;
  state.ownersSort = { ...snap.ownersSort };
  state.ownersGridSort = snap.ownersGridSort;
  state.ownersHighlight = snap.ownersHighlight;
  state.naView = snap.naView;
  state.naGaps = { ...snap.naGaps };
  state.naRisks = { ...snap.naRisks };
  state.naFocusLabel = snap.naFocusLabel;
  state.recordsPanel = snap.recordsPanel ? { ...snap.recordsPanel } : null;
  state.mapFocus = snap.mapFocus ? { ...snap.mapFocus } : null;
  state.linkedCompositionKey = snap.linkedCompositionKey;
  state.coverageFocus = snap.coverageFocus ? { ...snap.coverageFocus } : null;
}

function saveCurrentTab(state: CoverageState) {
  state.tabState[canonicalCoverageView(state.view)] = snapshotOf(state);
}

/** Prototype render: Overview is always portfolio-wide. */
function forceOverviewScope(state: CoverageState) {
  if (canonicalCoverageView(state.view) !== "overview") return;
  state.filters.account = "all";
  state.filters.role = "all";
  state.filters.owner = "all";
}

/**
 * Click-through: save the source tab, open the destination with fresh filters,
 * and carry account / role / owner (the scope that produced the number).
 */
function beginDrill(state: CoverageState, view: CoverageView) {
  const src = { ...state.filters };
  saveCurrentTab(state);
  applyTabSnapshot(state, undefined);
  state.filters.account = src.account;
  state.filters.role = src.role;
  state.filters.owner = src.owner;
  state.view = canonicalCoverageView(view);
  state.keptFrom = null;
  forceOverviewScope(state);
}

const initialState: CoverageState = {
  accounts: [],
  rows: [],
  attentionItems: [],
  ownerCapacities: [],
  capacityByGrade: [],
  capacityConfigured: true,
  loading: false,
  error: null,
  view: "overview",
  accountsMode: "heatmap",
  accountsSort: null,
  ownersFocus: null,
  ownersMode: "summary",
  ownersSort: { ...DEFAULT_OWNERS_SORT },
  ownersGridSort: null,
  ownersHighlight: null,
  accountModalSlide: "coverage",
  keptFrom: null,
  tabState: {},
  filters: { ...EMPTY_FILTERS },
  analysis: { ...EMPTY_ANALYSIS },
  groupBy: "condition",
  compositionLens: "readiness",
  compositionReturn: null,
  accountDetailId: null,
  linkedCompositionKey: null,
  returnContext: null,
  recordsPanel: null,
  mapFocus: null,
  readinessSegment: "all",
  ownerPortfolio: null,
  capacitySettingsOpen: false,
  naView: "gaps",
  naGaps: { ...DEFAULT_NA_GAPS },
  naRisks: { ...DEFAULT_NA_RISKS },
  naFocusLabel: null,
  coverageFocus: null,
};

export const fetchPortfolioCoverage = createAsyncThunk(
  "coverage/fetchPortfolio",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.portfolioCoverage());
      return extractContent(response);
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to load portfolio coverage"
      );
    }
  }
);

const coverageSlice = createSlice({
  name: "coverage",
  initialState,
  reducers: {
    setCoverageView(state, action: { payload: CoverageView }) {
      const raw = action.payload;
      const next = canonicalCoverageView(raw);
      if (next === state.view) {
        if (raw === "gaps") state.naView = "gaps";
        return;
      }
      saveCurrentTab(state);
      applyTabSnapshot(state, state.tabState[next]);
      state.view = next;
      state.keptFrom = null;
      if (raw === "gaps") state.naView = "gaps";
      forceOverviewScope(state);
    },
    setNaView(state, action: { payload: NaView }) {
      state.naView = action.payload;
    },
    setNaGaps(
      state,
      action: { payload: { key: keyof NaGapsState; value: string } }
    ) {
      const { key, value } = action.payload;
      if (key === "type") {
        state.naGaps.type = value;
        state.naGaps.lifecycle = "all";
      } else if (key === "age") {
        state.naGaps.age = value;
      } else if (key === "groupBy") {
        state.naGaps.groupBy = value as NaGapsState["groupBy"];
      } else if (key === "sort") {
        state.naGaps.sort = value as NaGapsState["sort"];
      } else if (key === "lifecycle") {
        state.naGaps.lifecycle = value as NaGapsState["lifecycle"];
      }
      state.naFocusLabel = null;
    },
    setNaRisks(
      state,
      action: { payload: { key: keyof NaRisksState; value: string } }
    ) {
      const { key, value } = action.payload;
      if (key === "type") state.naRisks.type = value;
      else if (key === "groupBy") {
        state.naRisks.groupBy = value as NaRisksState["groupBy"];
      }
      state.naFocusLabel = null;
    },
    openNeedsAttention(
      state,
      action: {
        payload: {
          naView?: NaView;
          gapType?: string;
          /** Continuity Orphaned / Covered elsewhere → Gaps lifecycle chip. */
          lifecycle?: "all" | "orphaned" | "covered";
          riskType?: string;
          accountId?: string | null;
          ownerId?: string | null;
          focusLabel?: string | null;
          readinessSegment?: string;
          /** Keep Back to composition when drilling from Overview lenses. */
          compositionReturn?: {
            lensId: string;
            lensLabel: string;
            categoryKey: string;
            categoryLabel: string;
          } | null;
        };
      }
    ) {
      const p = action.payload;
      const fromView = canonicalCoverageView(state.view);
      beginDrill(state, "attention");
      state.naView = p.naView || "gaps";
      if (p.gapType) state.naGaps.type = p.gapType;
      if (p.lifecycle) state.naGaps.lifecycle = p.lifecycle;
      if (p.riskType) state.naRisks.type = p.riskType;
      if (p.accountId) state.filters.account = p.accountId;
      if (p.ownerId) {
        state.filters.owner = p.ownerId;
        state.filters.account = "all";
        state.filters.role = "all";
      }
      if (p.readinessSegment) state.readinessSegment = p.readinessSegment;
      state.naFocusLabel = null;
      state.coverageFocus = p.focusLabel
        ? { view: "attention", fromView: fromView, label: p.focusLabel }
        : null;
      state.compositionReturn =
        p.compositionReturn !== undefined ? p.compositionReturn : null;
    },
    clearNaFocus(state) {
      state.naFocusLabel = null;
      state.readinessSegment = "all";
    },
    setCoverageAccountsMode(
      state,
      action: { payload: "heatmap" | "strength" }
    ) {
      state.accountsMode = action.payload;
    },
    setCoverageOwnersMode(state, action: { payload: OwnersMode }) {
      state.ownersMode = action.payload;
      state.ownersHighlight = null;
    },
    setCoverageOwnersSort(state, action: { payload: OwnersSortState }) {
      state.ownersSort = action.payload;
    },
    setCoverageOwnersGridSort(state, action: { payload: string | null }) {
      state.ownersGridSort =
        action.payload != null && state.ownersGridSort === action.payload
          ? null
          : action.payload;
    },
    setCoverageOwnersHighlight(state, action: { payload: string | null }) {
      state.ownersHighlight = action.payload;
    },
    ownersViewInGrid(state, action: { payload: string }) {
      state.ownersMode = "grid";
      state.ownersHighlight = action.payload;
    },
    setAccountModalSlide(state, action: { payload: "coverage" | "strength" }) {
      state.accountModalSlide = action.payload;
    },
    setCoverageFilter(
      state,
      action: { payload: { key: keyof CoverageFilters; value: string } }
    ) {
      const key = action.payload.key;
      if (
        state.view === "overview" &&
        (key === "account" || key === "role" || key === "owner")
      ) {
        return;
      }
      state.filters[key] = action.payload.value;
      state.compositionReturn = null;
      state.coverageFocus = null;
      if (key === "status") state.keptFrom = null;
    },
    setCoverageStatusView(
      state,
      action: {
        payload: {
          status: string;
          view: CoverageState["view"];
          readinessSegment?: string;
          accountsMode?: "heatmap" | "strength";
        };
      }
    ) {
      beginDrill(state, action.payload.view);
      state.filters.status = action.payload.status;
      if (action.payload.accountsMode) {
        state.accountsMode = action.payload.accountsMode;
      }
      if (action.payload.readinessSegment != null) {
        state.readinessSegment = action.payload.readinessSegment;
      } else if (action.payload.status === "setup") {
        state.readinessSegment = "setup";
      }
    },
    clearCoverageFilter(state, action: { payload: keyof CoverageFilters }) {
      state.filters[action.payload] = "all";
      state.compositionReturn = null;
      state.coverageFocus = null;
      const stillHas =
        state.filters.account !== "all" ||
        state.filters.role !== "all" ||
        state.filters.owner !== "all";
      if (!stillHas) state.keptFrom = null;
    },
    clearCoverageFilters(state) {
      state.filters = { ...EMPTY_FILTERS };
      state.analysis = { ...EMPTY_ANALYSIS };
      state.groupBy = "condition";
      state.accountDetailId = null;
      state.compositionReturn = null;
      state.recordsPanel = null;
      state.mapFocus = null;
      state.readinessSegment = "all";
      state.accountsMode = "heatmap";
      state.ownersMode = "summary";
      state.ownersSort = { ...DEFAULT_OWNERS_SORT };
      state.ownersGridSort = null;
      state.ownersHighlight = null;
      state.accountsSort = null;
      state.ownersFocus = null;
      state.keptFrom = null;
      state.coverageFocus = null;
      state.naFocusLabel = null;
      state.naGaps = { ...DEFAULT_NA_GAPS };
      state.naRisks = { ...DEFAULT_NA_RISKS };
    },
    setAnalysisFilter(
      state,
      action: { payload: { key: keyof AnalysisFilters; value: string } }
    ) {
      state.analysis[action.payload.key] = action.payload.value;
      if (
        action.payload.key === "condition" &&
        action.payload.value === "all"
      ) {
        state.groupBy = "condition";
      }
    },
    setAttentionCondition(state, action: { payload: string }) {
      const selected = state.analysis.condition === action.payload;
      state.analysis.condition = selected ? "all" : action.payload;
      state.groupBy = selected ? "condition" : "account";
    },
    setCoverageGroupBy(state, action: { payload: string }) {
      state.groupBy = action.payload;
    },
    clearAnalysisFilters(state) {
      state.analysis = { ...EMPTY_ANALYSIS };
      state.groupBy = "condition";
    },
    setCompositionLens(state, action: { payload: string }) {
      if (state.compositionLens === action.payload) {
        state.filters.status = "all";
        state.readinessSegment = "all";
        state.compositionReturn = null;
        return;
      }
      state.compositionLens = action.payload;
    },
    drillFromComposition(
      state,
      action: {
        payload: {
          lensId: string;
          lensLabel: string;
          categoryKey: string;
          categoryLabel: string;
          view: CoverageState["view"];
          status?: string;
          readinessSegment?: string;
          accountsMode?: "heatmap" | "strength";
        };
      }
    ) {
      beginDrill(state, action.payload.view);
      state.compositionReturn = {
        lensId: action.payload.lensId,
        lensLabel: action.payload.lensLabel,
        categoryKey: action.payload.categoryKey,
        categoryLabel: action.payload.categoryLabel,
      };
      if (action.payload.status) state.filters.status = action.payload.status;
      if (action.payload.readinessSegment) {
        state.readinessSegment = action.payload.readinessSegment;
      }
      state.accountsMode =
        action.payload.accountsMode ||
        (action.payload.lensId === "evidence" ? "strength" : "heatmap");
      if (action.payload.status === "two-owners") {
        state.accountsSort = { key: "two", dir: "desc" };
      }
      state.accountModalSlide =
        action.payload.lensId === "evidence" ? "strength" : "coverage";
    },
    returnToComposition(state) {
      const origin = state.compositionReturn;
      saveCurrentTab(state);
      applyTabSnapshot(state, state.tabState.overview);
      state.view = "overview";
      if (origin) state.compositionLens = origin.lensId;
      state.compositionReturn = null;
      forceOverviewScope(state);
    },
    setLinkedCompositionKey(state, action: { payload: string | null }) {
      state.linkedCompositionKey = action.payload;
    },
    focusCoverageAccount(state, action: { payload: string }) {
      if (state.view === "overview") return;
      state.compositionReturn = null;
      state.filters.account = action.payload;
    },
    setAccountDetail(state, action: { payload: string | null }) {
      state.accountDetailId = action.payload;
      if (action.payload == null) {
        /* keep panel */
      }
    },
    setCoverageReturnContext(
      state,
      action: { payload: CoverageReturnContext | null }
    ) {
      state.returnContext = action.payload;
    },
    setRecordsPanel(
      state,
      action: {
        payload: CoverageState["recordsPanel"];
      }
    ) {
      state.recordsPanel = action.payload;
    },
    setMapFocus(
      state,
      action: {
        payload: CoverageState["mapFocus"];
      }
    ) {
      state.mapFocus = action.payload;
      if (action.payload?.accountId && state.view !== "overview") {
        saveCurrentTab(state);
        applyTabSnapshot(state, state.tabState.overview);
        state.view = "overview";
        forceOverviewScope(state);
      }
    },
    setReadinessSegment(state, action: { payload: string }) {
      state.readinessSegment = action.payload;
      // Clearing the readiness drill filter also drops the composition return banner.
      if (action.payload === "all") {
        state.compositionReturn = null;
      }
    },
    setOwnerPortfolio(
      state,
      action: {
        payload: CoverageState["ownerPortfolio"];
      }
    ) {
      state.ownerPortfolio = action.payload;
    },
    setOwnerPortfolioFilter(
      state,
      action: { payload: OwnersPortfolioFilter | "all" | null }
    ) {
      if (!state.ownerPortfolio) return;
      const next =
        action.payload === "all" || action.payload == null
          ? null
          : action.payload;
      state.ownerPortfolio = {
        ...state.ownerPortfolio,
        filter: next,
      };
    },
    toggleOwnerPortfolioFilter(
      state,
      action: { payload: OwnersPortfolioFilter }
    ) {
      if (!state.ownerPortfolio) return;
      const current = state.ownerPortfolio.filter ?? null;
      state.ownerPortfolio = {
        ...state.ownerPortfolio,
        filter: current === action.payload ? null : action.payload,
      };
    },
    openNotSetUpAccounts(state) {
      beginDrill(state, "attention");
      state.naView = "gaps";
      state.naGaps.type = "setup";
      state.readinessSegment = "not-set-up";
      state.naFocusLabel = null;
    },
    openUnassessedRelationships(state) {
      beginDrill(state, "accounts");
      state.filters.status = "unassessed";
      state.accountsMode = "strength";
      state.accountModalSlide = "strength";
      state.naFocusLabel = null;
    },
    /** Heatmap header: first click sorts toward the worst value, second click reverses. */
    sortCoverageAccounts(state, action: { payload: string }) {
      const key = action.payload;
      const worst = HEAT_WORST_DIR[key] || "asc";
      const cur = state.accountsSort;
      state.accountsSort =
        cur && cur.key === key
          ? { key, dir: cur.dir === "asc" ? "desc" : "asc" }
          : { key, dir: worst };
    },
    /** Contact coverage tile: Accounts heatmap, lowest coverage first. */
    openAccountsSorted(
      state,
      action: { payload: { key: string; dir: "asc" | "desc" } }
    ) {
      beginDrill(state, "accounts");
      state.accountsMode = "heatmap";
      state.accountsSort = action.payload;
    },
    /** Single-threaded tile: Accounts with those contacts, busiest first. */
    openSingleThreaded(state) {
      beginDrill(state, "accounts");
      state.filters.status = "single-owner";
      state.accountsMode = "heatmap";
      state.accountsSort = { key: "single", dir: "desc" };
    },
    /** Account popup band: fresh Accounts drill scoped to that account. */
    drillAccountFocus(
      state,
      action: {
        payload: {
          accountId: string;
          status?: string;
          accountsMode?: "heatmap" | "strength";
          /** Shown on the Focus chip, and kept after the side list closes. */
          focusLabel?: string;
        };
      }
    ) {
      beginDrill(state, "accounts");
      const p = action.payload;
      state.filters.role = "all";
      state.filters.owner = "all";
      if (p.status) state.filters.status = p.status;
      if (p.accountsMode) {
        state.accountsMode = p.accountsMode;
        state.accountModalSlide =
          p.accountsMode === "strength" ? "strength" : "coverage";
      }
      state.filters.account = p.accountId;
      state.coverageFocus = p.focusLabel
        ? { view: "accounts", fromView: "accounts", label: p.focusLabel }
        : null;
    },
    /** Focus chip ×: drop the click-through and show Accounts unfiltered. */
    clearCoverageFocus(state) {
      state.coverageFocus = null;
      state.compositionReturn = null;
      state.naFocusLabel = null;
      state.filters = { ...EMPTY_FILTERS };
      state.readinessSegment = "all";
      state.recordsPanel = null;
      state.accountsMode = "heatmap";
      state.accountsSort = null;
      state.naGaps = { ...DEFAULT_NA_GAPS };
      state.naRisks = { ...DEFAULT_NA_RISKS };
    },
    /** ← Back: restore the tab as it was before this click-through. */
    restoreCoverageFocus(state) {
      const from = canonicalCoverageView(
        state.coverageFocus?.fromView || "accounts"
      );
      if (from === canonicalCoverageView(state.view)) {
        applyTabSnapshot(state, state.tabState[from]);
        state.coverageFocus = null;
        return;
      }
      saveCurrentTab(state);
      applyTabSnapshot(state, state.tabState[from]);
      state.view = from;
      state.coverageFocus = null;
      forceOverviewScope(state);
    },
    /** KPI tile: Owners summary, carrying the source scope. */
    openOwnersSummary(state) {
      beginDrill(state, "owners");
      state.ownersMode = "summary";
      state.ownersFocus = "capacity";
    },
    setCapacitySettingsOpen(state, action: { payload: boolean }) {
      state.capacitySettingsOpen = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPortfolioCoverage.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPortfolioCoverage.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = action.payload?.accounts || [];
        state.rows = action.payload?.rows || [];
        state.attentionItems = action.payload?.attention_items || [];
        state.ownerCapacities = action.payload?.owner_capacities || [];
        state.capacityByGrade = action.payload?.capacity_by_grade || [];
        state.capacityConfigured =
          action.payload?.capacity_configured !== false;
      })
      .addCase(fetchPortfolioCoverage.rejected, (state, action) => {
        state.loading = false;
        state.error = String(
          action.payload || "Failed to load portfolio coverage"
        );
      });
  },
});

export const {
  setCoverageView,
  setCoverageAccountsMode,
  sortCoverageAccounts,
  setCoverageOwnersMode,
  setCoverageOwnersSort,
  setCoverageOwnersGridSort,
  setCoverageOwnersHighlight,
  ownersViewInGrid,
  setAccountModalSlide,
  setCoverageFilter,
  setCoverageStatusView,
  clearCoverageFilter,
  clearCoverageFilters,
  setAnalysisFilter,
  setAttentionCondition,
  setCoverageGroupBy,
  clearAnalysisFilters,
  setCompositionLens,
  drillFromComposition,
  returnToComposition,
  setLinkedCompositionKey,
  focusCoverageAccount,
  setAccountDetail,
  setCoverageReturnContext,
  setRecordsPanel,
  setMapFocus,
  setReadinessSegment,
  openNotSetUpAccounts,
  openUnassessedRelationships,
  openAccountsSorted,
  openSingleThreaded,
  drillAccountFocus,
  clearCoverageFocus,
  restoreCoverageFocus,
  openOwnersSummary,
  setCapacitySettingsOpen,
  setOwnerPortfolio,
  setOwnerPortfolioFilter,
  toggleOwnerPortfolioFilter,
  setNaView,
  setNaGaps,
  setNaRisks,
  openNeedsAttention,
  clearNaFocus,
} = coverageSlice.actions;

export default coverageSlice.reducer;
